// Built-in Libraries

import { PencilIcon } from '@heroicons/react/24/solid';
import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router';
import { type GetCommentParams, getPartnerProposalComments, postComment } from '@/api/comments';
import Comments from '@/components/Comments';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import HistoryTimeline from '@/components/UI/HistoryTimeline';
// Custom Hooks
import { useDocument } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
import { useProject } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { useUsers } from '@/hooks/ReactQueryHooks/useUsersQuery';
// Assets
import { convertEnumToText, formatDate3, getInitials } from '@/utils';
import Approvals from '../../components/Approvals/index.tsx';
import { EditDocument } from '../EditDocument';

const DocumentDetails = () => {
  const { projectId, drawId, documentId } = useParams<string>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const project = fetchProject?.data?.data;

  const fetchUsers = useUsers({ params: { active: 'true' } });

  const users = fetchUsers.data?.data;

  const fetchDocument = useDocument({
    projectId: Number(projectId),
    drawId: Number(drawId),
    documentId: Number(documentId),
  });
  const document = fetchDocument?.data?.data;

  const createdBy = users?.find((member: any) => member?.id === document?.createdById);

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: documentId!,
          resource_type: 'ProjectDrawDocument',
          ...values,
        },
      });
      return response;
    },
    [documentId]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: documentId!,
          resource_type: 'ProjectDrawDocument',
        },
      });
    },
    [documentId]
  );

  const handleModel = () => {
    setIsEditing(!isEditing);
  };

  return (
    <>
      {isEditing && <EditDocument document={document} handleModal={handleModel} projectName={project?.name || ''} />}
      {document && (
        <div className='min-h-full max-w-screen-2xl mx-auto'>
          <main>
            <div className='sm:px-6 mt-8 w-full mx-auto grid grid-cols-12 gap-6'>
              <div className='space-y-6 col-span-12 xl:col-span-8'>
                <section>
                  <div className='bg-white shadow sm:rounded-lg px-4 py-5'>
                    <div className='py-2 px-4 flex justify-between items-center border-b-2'>
                      <h2 className='capitalize'>{document?.name}</h2>
                      <div className='flex justify-between items-center gap-3'>
                        <Badge status={document?.status} text={convertEnumToText(document?.status)} />
                        {document?.status !== 'APPROVED' && (
                          <PencilIcon className='h-5 w-5 text-neutralColor-normal cursor-pointer' onClick={handleModel} />
                        )}
                      </div>
                    </div>
                    <div className='py-4'>
                      {project && createdBy && (
                        <div className='flex flex-col justify-between px-2'>
                          <h4 className='font-semibold'>Submitted By</h4>
                          <div className='flex pt-2'>
                            <Link to={`/app/administration/users/${createdBy?.id}`} className='flex items-center gap-2'>
                              <ImageCircularAvatar
                                customClass='h-16 w-16'
                                imageUrl={createdBy?.profileImageUrl as string}
                                fallbackText={getInitials(createdBy?.name)}
                              />
                              <div>
                                <p className='text-base font-semibold'>{createdBy?.name}</p>
                                <p className='text-neutralColor-normal text-sm'>{(createdBy as any)?.title}</p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      )}
                      <div className='grid grid-cols-2 py-6 border-b-2 px-2'>
                        <div className='col-span-1'>
                          <div className='flex flex-col items-start gap-3'>
                            <div>
                              <span className='text-neutralColor-normal'>Document Type:</span>
                              <span className=''> {convertEnumToText(document?.documentTypeSlug)}</span>
                            </div>
                          </div>
                        </div>
                        <div className='col-span-1 justify-self-center'>
                          <div className='flex flex-col items-start gap-3'>
                            <div>
                              <span className='text-neutralColor-normal'>Submitted:</span>
                              <span> {formatDate3(document?.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col space-y-2 py-4 px-2'>
                        <h3 className='font-semibold'>Summary</h3>
                        <p className='text-neutralColor-normal'>{document?.summary}</p>
                      </div>
                    </div>
                  </div>
                </section>
                <Comments getComments={getComments} postComments={postComments} queryKey='document-comments' />
              </div>
              <div className='space-y-6 hidden xl:block xl:col-span-4'>
                <Approvals approvals={document?.approval} members={users} type='document' />
                <HistoryTimeline activity={document} resourceType='ProjectDrawDocument' />
              </div>
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default DocumentDetails;
